import config from '../config';
import selectedEnvironment from '../env';
import sharedGet from './sharedGet';

const API_URL = `${config[selectedEnvironment].API_URL}/annual-report`;

const request = (path, token, options = {}) =>
    ((options.method ?? 'GET') === 'GET'
        ? sharedGet(`${API_URL}${path}`, token, () => send(path, token, options))
        : send(path, token, options));

const send = async (path, token, { method = 'GET', body } = {}) => {
    const response = await fetch(`${API_URL}${path}`, {
        method,
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: body === undefined ? undefined : JSON.stringify(body),
    });

    if (!response.ok) {
        const message = await response.text();
        const error = new Error(message || `HTTP ${response.status}`);
        error.status = response.status;
        throw error;
    }

    const text = await response.text();
    return text ? JSON.parse(text) : null;
};

/**
 * GET that returns a file; resolves to `{ blob, fileName }` (file name from Content-Disposition when present).
 * `baseUrl` points it at another API (e.g. expected-tax).
 */
const requestFile = async (path, token, baseUrl = API_URL) => {
    const response = await fetch(`${baseUrl}${path}`, {
        headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
        const message = await response.text();
        const error = new Error(message || `HTTP ${response.status}`);
        error.status = response.status;
        throw error;
    }

    const disposition = response.headers.get('Content-Disposition') || '';
    const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    const plainMatch = disposition.match(/filename="?([^";]+)"?/i);
    const fileName = utf8Match ? decodeURIComponent(utf8Match[1]) : (plainMatch ? plainMatch[1] : null);
    return { blob: await response.blob(), fileName };
};

const caseYearPath = (caseId, year) => `/${encodeURIComponent(caseId)}/${encodeURIComponent(year)}`;

const getProfitAndLoss = (caseId, year, token) =>
    request(`${caseYearPath(caseId, year)}/profit-and-loss`, token);

/**
 * The saved P&L with each line's 6111 code. `includeLive` reads the source again for per-line differences;
 * `liveSource` (a ProfitAndLossSource) reads that report instead, even before the P&L was fetched;
 * `filterByDocumentDate` is its date basis (the saved fetch's when omitted).
 * Case types without an annual report answer `{ eligible: false }`.
 */
const getProfitAndLossComparison = (caseId, year, includeLive, token, liveSource = null, filterByDocumentDate = null) => {
    const params = new URLSearchParams({ includeLive: includeLive ? 'true' : 'false' });
    if (liveSource !== null && liveSource !== undefined) params.set('liveSource', String(liveSource));
    if (typeof filterByDocumentDate === 'boolean') params.set('filterByDocumentDate', String(filterByDocumentDate));
    return request(`${caseYearPath(caseId, year)}/profit-and-loss/comparison?${params}`, token);
};

/**
 * @param {{ sourceType: number, filterByDocumentDate: boolean, onlyProcessed: boolean,
 *           resetManualChanges?: boolean }} options `resetManualChanges` drops typed-in rows and manual amounts.
 */
const fetchProfitAndLoss = (caseId, year, options, token) =>
    request(`${caseYearPath(caseId, year)}/profit-and-loss/fetch`, token, { method: 'POST', body: options });

/** @param {Array<{ lineId: string, manualAmount: number|null, changeReason: string|null }>} lines */
const updateLines = (caseId, year, lines, token) =>
    request(`${caseYearPath(caseId, year)}/profit-and-loss/lines`, token, { method: 'PUT', body: { lines } });

const addManualLine = (caseId, year, line, token) =>
    request(`${caseYearPath(caseId, year)}/profit-and-loss/lines`, token, { method: 'POST', body: line });

const setLineCode6111 = (caseId, year, lineId, code6111Id, token) =>
    request(`${caseYearPath(caseId, year)}/profit-and-loss/lines/${encodeURIComponent(lineId)}/code6111`, token, {
        method: 'PUT',
        body: { code6111Id: code6111Id || null },
    });

const deleteLine = (caseId, year, lineId, token) =>
    request(`${caseYearPath(caseId, year)}/profit-and-loss/lines/${encodeURIComponent(lineId)}`, token, { method: 'DELETE' });

const getNotes = (caseId, { lineKeyType, lineKey, fromYear, toYear }, token) => {
    const query = new URLSearchParams({
        lineKeyType: String(lineKeyType),
        lineKey: String(lineKey),
        fromYear: String(fromYear),
        toYear: String(toYear),
    });
    return request(`/${encodeURIComponent(caseId)}/notes?${query.toString()}`, token);
};

const upsertNote = (caseId, year, note, token) =>
    request(`${caseYearPath(caseId, year)}/notes`, token, { method: 'PUT', body: note });

const deleteNote = (caseId, noteId, token) =>
    request(`/${encodeURIComponent(caseId)}/notes/${encodeURIComponent(noteId)}`, token, { method: 'DELETE' });

const downloadProfitAndLossPdf = (caseId, year, changesOnly, token) =>
    requestFile(`${caseYearPath(caseId, year)}/profit-and-loss/pdf?changesOnly=${changesOnly ? 'true' : 'false'}`, token);

/** Sort-code source only: the updated P&L in the sort-code report layout, as `{ html }`. */
const getProfitAndLossReportHtml = (caseId, year, showPct, token) =>
    request(`${caseYearPath(caseId, year)}/profit-and-loss/report-html?showPct=${showPct ? 'true' : 'false'}`, token);

const downloadProfitAndLossReportPdf = (caseId, year, showPct, token) =>
    requestFile(`${caseYearPath(caseId, year)}/profit-and-loss/report-pdf?showPct=${showPct ? 'true' : 'false'}`, token);

/** Account-cards source only: the year's trial balance moved to the saved amounts. */
const getProfitAndLossReportTrialBalance = (caseId, year, token) =>
    request(`${caseYearPath(caseId, year)}/profit-and-loss/report-trial-balance`, token);

/** @param {{ lineIds: string[], section: number|null, category: number|null }} assignment */
const assignAnnexBLines = (caseId, year, assignment, token) =>
    request(`${caseYearPath(caseId, year)}/profit-and-loss/lines/annex-b`, token, { method: 'PUT', body: assignment });

const getAnnexB = (caseId, year, token) =>
    request(`${caseYearPath(caseId, year)}/annex-b`, token);

const saveAnnexBSection = (caseId, year, section, detail, token) =>
    request(`${caseYearPath(caseId, year)}/annex-b/sections/${encodeURIComponent(section)}`, token, { method: 'PUT', body: detail });

const addAnnexBItem = (caseId, year, item, token) =>
    request(`${caseYearPath(caseId, year)}/annex-b/items`, token, { method: 'POST', body: item });

const updateAnnexBItem = (caseId, year, itemId, item, token) =>
    request(`${caseYearPath(caseId, year)}/annex-b/items/${encodeURIComponent(itemId)}`, token, { method: 'PUT', body: item });

const deleteAnnexBItem = (caseId, year, itemId, token) =>
    request(`${caseYearPath(caseId, year)}/annex-b/items/${encodeURIComponent(itemId)}`, token, { method: 'DELETE' });

const resetAnnexB = (caseId, year, token) =>
    request(`${caseYearPath(caseId, year)}/annex-b`, token, { method: 'DELETE' });

const downloadAnnexBFormPdf = (caseId, year, token) =>
    requestFile(`${caseYearPath(caseId, year)}/annex-b/form-pdf`, token);

const downloadAnnexBReportPdf = (caseId, year, token) =>
    requestFile(`${caseYearPath(caseId, year)}/annex-b/report-pdf`, token);

/** נספח ג (form 1322): every annex of the year with its 867 accounts and computed results. */
const getAnnexC = (caseId, year, token) =>
    request(`${caseYearPath(caseId, year)}/annex-c`, token);

const addAnnexCForm = (caseId, year, form, token) =>
    request(`${caseYearPath(caseId, year)}/annex-c/forms`, token, { method: 'POST', body: form });

const updateAnnexCForm = (caseId, year, formId, form, token) =>
    request(`${caseYearPath(caseId, year)}/annex-c/forms/${encodeURIComponent(formId)}`, token, { method: 'PUT', body: form });

const deleteAnnexCForm = (caseId, year, formId, token) =>
    request(`${caseYearPath(caseId, year)}/annex-c/forms/${encodeURIComponent(formId)}`, token, { method: 'DELETE' });

const addAnnexCAccount = (caseId, year, formId, account, token) =>
    request(`${caseYearPath(caseId, year)}/annex-c/forms/${encodeURIComponent(formId)}/accounts`, token, { method: 'POST', body: account });

/** The account's entries replace all its lines. */
const updateAnnexCAccount = (caseId, year, accountId, account, token) =>
    request(`${caseYearPath(caseId, year)}/annex-c/accounts/${encodeURIComponent(accountId)}`, token, { method: 'PUT', body: account });

const deleteAnnexCAccount = (caseId, year, accountId, token) =>
    request(`${caseYearPath(caseId, year)}/annex-c/accounts/${encodeURIComponent(accountId)}`, token, { method: 'DELETE' });

const downloadAnnexCFormPdf = (caseId, year, token) =>
    requestFile(`${caseYearPath(caseId, year)}/annex-c/form-pdf`, token);

const getAdditionalInfo = (caseId, year, token) =>
    request(`${caseYearPath(caseId, year)}/additional-info`, token);

/** Sections left out of `info` (details, credit points, tracking) stay as saved. */
const saveAdditionalInfo = (caseId, year, info, token) =>
    request(`${caseYearPath(caseId, year)}/additional-info`, token, { method: 'PUT', body: info });

/** Replaces the submission confirmation (PDF or image); resolves to the saved tracking. */
const uploadSubmissionConfirmation = async (caseId, year, file, token) => {
    const form = new FormData();
    form.append('file', file);
    const response = await fetch(`${API_URL}${caseYearPath(caseId, year)}/submission-confirmation`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
    });
    if (!response.ok) {
        const error = new Error((await response.text()) || `HTTP ${response.status}`);
        error.status = response.status;
        throw error;
    }
    return response.json();
};

/** Resolves to `{ url, fileName }`; the link expires after a short while, so ask for it on every view. */
const getSubmissionConfirmation = (caseId, year, token) =>
    request(`${caseYearPath(caseId, year)}/submission-confirmation`, token);

const deleteSubmissionConfirmation = (caseId, year, token) =>
    request(`${caseYearPath(caseId, year)}/submission-confirmation`, token, { method: 'DELETE' });

const getQuestionnairePrefill = (caseId, year, token) =>
    request(`${caseYearPath(caseId, year)}/questionnaire-prefill`, token);

const getDepreciationTransfer = (caseId, year, token) =>
    request(`${caseYearPath(caseId, year)}/depreciation-transfer`, token);

/**
 * Writes the matching report (דוח התאמה למס) rows: הפחת form 1342, הוסף the book depreciation back, הפחת form 1343.
 * @param {{ includeDepreciation: boolean, addBackBookDepreciation: boolean,
 *           includeAdditionalDeduction: boolean }} options
 */
const transferDepreciation = (caseId, year, options, token) =>
    request(`${caseYearPath(caseId, year)}/depreciation-transfer`, token, { method: 'POST', body: options });

/** טופס 1343 lives under the Assets API (it is not limited to עוסק מורשה). */
const ASSETS_API_URL = API_URL.replace(/\/annual-report$/, '/Assets/Form1343');

const getForm1343 = async (caseId, year, token) => {
    const response = await fetch(`${ASSETS_API_URL}/${encodeURIComponent(caseId)}/${encodeURIComponent(year)}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
        const error = new Error((await response.text()) || `HTTP ${response.status}`);
        error.status = response.status;
        throw error;
    }
    return response.json();
};

const setForm1343Exclusion = async (caseId, assetId, excluded, token) => {
    const response = await fetch(`${ASSETS_API_URL}/${encodeURIComponent(caseId)}/exclusion`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ assetId, excluded }),
    });
    if (!response.ok) {
        const error = new Error((await response.text()) || `HTTP ${response.status}`);
        error.status = response.status;
        throw error;
    }
};

/** Rows from the year's receipts (ClientWithholdingModal numbers, reported amounts win). */
const getWithholdingSummary = (caseId, year, token) =>
    request(`${caseYearPath(caseId, year)}/withholding-summary`, token);

/** The saved ניכויים במקור table and approval letter, or the receipts rows and standard wording when none is saved. */
const getWithholdingOpinion = (caseId, year, token) =>
    request(`${caseYearPath(caseId, year)}/withholding-opinion`, token);

/** @param {{ rows: object[], letterText: string|null, useAccountantSignature: boolean, removeCustomSignature?: boolean }} body */
const saveWithholdingOpinion = (caseId, year, body, token) =>
    request(`${caseYearPath(caseId, year)}/withholding-opinion`, token, { method: 'PUT', body });

/** Resolves to `{ letterText, usedAi, reason }`; the wording keeps {total}/{year} placeholders, never amounts. */
const draftWithholdingLetter = (caseId, year, body, token) =>
    request(`${caseYearPath(caseId, year)}/withholding-opinion/draft`, token, { method: 'POST', body });

/** @param {string} image a PNG or JPEG data URI */
const saveWithholdingSignature = (caseId, year, image, token) =>
    request(`${caseYearPath(caseId, year)}/withholding-opinion/signature`, token, { method: 'POST', body: { image } });

const downloadWithholdingOpinionPdf = (caseId, year, token) =>
    requestFile(`${caseYearPath(caseId, year)}/withholding-opinion/pdf`, token);

/** Writes the business rows to 040 (question 143) and the salary rows to 042 (question 140). */
const applyWithholdingOpinionTo1301 = (caseId, year, token) =>
    request(`${caseYearPath(caseId, year)}/withholding-opinion/apply-1301`, token, { method: 'POST' });

const AnnualReportService = {
    getWithholdingSummary,
    getWithholdingOpinion,
    saveWithholdingOpinion,
    draftWithholdingLetter,
    saveWithholdingSignature,
    downloadWithholdingOpinionPdf,
    applyWithholdingOpinionTo1301,
    getDepreciationTransfer,
    transferDepreciation,
    getForm1343,
    setForm1343Exclusion,
    requestFile,
    downloadProfitAndLossPdf,
    getProfitAndLossReportHtml,
    downloadProfitAndLossReportPdf,
    getProfitAndLossReportTrialBalance,
    assignAnnexBLines,
    getAnnexB,
    saveAnnexBSection,
    addAnnexBItem,
    updateAnnexBItem,
    deleteAnnexBItem,
    resetAnnexB,
    downloadAnnexBFormPdf,
    downloadAnnexBReportPdf,
    getAnnexC,
    addAnnexCForm,
    updateAnnexCForm,
    deleteAnnexCForm,
    addAnnexCAccount,
    updateAnnexCAccount,
    deleteAnnexCAccount,
    downloadAnnexCFormPdf,
    getAdditionalInfo,
    saveAdditionalInfo,
    uploadSubmissionConfirmation,
    getSubmissionConfirmation,
    deleteSubmissionConfirmation,
    getQuestionnairePrefill,
    getProfitAndLoss,
    getProfitAndLossComparison,
    fetchProfitAndLoss,
    updateLines,
    addManualLine,
    setLineCode6111,
    deleteLine,
    getNotes,
    upsertNote,
    deleteNote,
};

export default AnnualReportService;
